import { Fragment, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { PaxPicker } from "../widgets";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import { TranslationContext, countryCodes } from "../../../umbilical_tools";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";
import { Formik, Form } from "formik";
import {
  AirportAutoCompleteField,
  DatepickerField,
  POIAutoCompleteInputField,
  ReactSelectField,
} from "../../forms/inputGroup/helpers";
import { createInitialValues, validateTransferSearch } from "./helpers";
import jssVariables from "../../../jss_setup";
import { useTransferActions, useTransfersInitSearch } from "../../../hooks/transfers";
import { SearchButton } from "../../common/Button";

export function tripModeOptions(t) {
  return [
    {
      value: "air_to_poi",
      label: t("airportToPoint"),
    },
    {
      value: "poi_to_air",
      label: t("pointToAirport"),
    },
  ];
}

const styles = createUseStyles({
  transferSearchForm: (props) => ({
    display: "grid",
    gridTemplateRows: "1fr max-content",
    gridGap: jssVariables.half_gap,
    padding: jssVariables.normal_gap,
    ..._.get(props, "customStyles.transferSearchForm", {}),
  }),
  formContainer: (props) => ({
    display: "grid",
    gridTemplateRows: "repeat(3, max-content)",
    gridTemplateColumns: "1fr",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    alignItems: "center",
    ..._.get(props, "customStyles.formContainer", {}),
  }),
  searchBtnContr: (props) => ({
    justifySelf: "center",
    paddingTop: jssVariables.normal_gap,
    ..._.get(props, "customStyles.searchBtnContainer", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    transferSearchForm: (props) => ({
      gridTemplateRows: "1fr",
      gridTemplateColumns: "1fr max-content",
      alignItems: "end",
      padding: 0,
      ..._.get(props, "customStyles.transferSearchForm.smallscreen", {}),
    }),
    formContainer: (props) => ({
      gridTemplateRows: "1fr",
      gridTemplateColumns: "max-content repeat(2, 1fr)",
      alignItems: "end",
      gridGap: jssVariables.half_gap,
      ..._.get(props, "customStyles.formContainer.smallscreen", {}),
    }),
    searchBtnContr: (props) => ({
      display: "grid",
      justifyContent: "center",
      ..._.get(props, "customStyles.searchBtnContainer.smallscreen", {}),
    }),
  },
});
export const TransferSearchForm = ({ setShowPaxPickerModal, customStyles }) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { t } = useContext(TranslationContext) as { t: (key: string) => string };
  const [initSearch] = useTransfersInitSearch({
    setLoadingSearchBtn: (value: boolean) => setIsSubmitting(value),
  });

  const { onClearState } = useTransferActions();

  const classes = styles({ customStyles });

  const search_form = useSelector((state) => state.TransfersSearchForm);

  const [tripMode, setTripMode] = useState(search_form.tripMode);

  function onClickPax() {
    setShowPaxPickerModal(true);
  }

  useEffect(() => {
    onClearState();
  }, [onClearState]);

  return (
    <Formik
      initialValues={createInitialValues({ search_form, tripMode, t })}
      validateOnBlur={false}
      validateOnMount={false}
      validate={async (values) => {
        if (values.tripMode !== tripMode) {
          setTripMode(values.tripMode.value);
        }
      }}
      onSubmit={async (values) => {
        if (!isSubmitting) {
          setIsSubmitting(true);

          const form = {
            ...values,
            origin:
              tripMode === "air_to_poi"
                ? values.origin.origin_meta[0]
                : {
                    ...values.origin.origin_meta,
                    country_code: countryCodes[values.origin.origin_meta.country],
                  },

            destination:
              tripMode === "air_to_poi"
                ? {
                    ...values.destination.destination_meta,
                    country_code: countryCodes[values.destination.destination_meta.country],
                  }
                : values.destination.destination_meta[0],
            inboundPickupDate: DateTime.fromISO(values.inboundPickupDate).toFormat(
              "yyyy-MM-dd'T'HH:mm:ss",
            ),
            tripMode: values.tripMode.value,
            init_search: true,
          };
          const hasErrors = await validateTransferSearch(form);

          if (hasErrors) {
            setIsSubmitting(false);
            return;
          }
          initSearch(form);
        }
      }}
    >
      <Form className={classes.transferSearchForm}>
        <div className={classes.formContainer}>
          <ReactSelectField
            id="tripMode"
            label={t("tripMode")}
            name="tripMode"
            options={tripModeOptions(t)}
            customStyles={_.get(customStyles, "inputGroupField", {})}
          />
          {tripMode === "air_to_poi" || tripMode === "round_trip" ? (
            <Fragment>
              <AirportAutoCompleteField
                id="airport"
                label={t("airport")}
                name="origin"
                placeholder={t("airport")}
                withErrors={false}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
              <POIAutoCompleteInputField
                id="destinationPoint"
                label={t("destinationPoint")}
                name={"destination"}
                types={["DEST"]}
                placeholder={"----"}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
            </Fragment>
          ) : (
            <Fragment>
              <POIAutoCompleteInputField
                label={t("originPoint")}
                name="origin"
                types={["DEST"]}
                placeholder={"----"}
                isInFilters={true}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
              <AirportAutoCompleteField
                label={t("airport")}
                name="destination"
                placeholder={t("airport")}
                withErrors={false}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
            </Fragment>
          )}

          <div />
          <DatepickerField
            id="inboundPickupDate"
            label={t("inboundPickupDate")}
            name="inboundPickupDate"
            selectsRange={false}
            dateFormat="MMM d, yyyy h:mm aa"
            minDate={DateTime.now().toISODate()}
            maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
            placeholder={"Mon DD, YYYY h:mm aa"}
            withPortal={true}
            showTimeSelect={true}
            customStyles={_.get(customStyles, "inputGroupField", {})}
            extraStyles={{}}
          />
          <PaxPicker
            id="paxPickerTrf"
            customStyles={_.get(customStyles, "inputGroupField", {})}
            adults={search_form.adults}
            childs={search_form.children}
            children_ages={search_form.children_ages}
            onClick={onClickPax}
            product_type="TRF"
          />
        </div>
        <div className={classes.searchBtnContr}>
          <SearchButton
            id="searchTrfBtn"
            text={t("search")}
            loading={isSubmitting}
            customStyles={_.get(customStyles, "searchBtn", {})}
          />
        </div>
      </Form>
    </Formik>
  );
};
TransferSearchForm.defaultProps = {};
TransferSearchForm.propTypes = {
  customStyles: PropTypes.object,
  setShowPaxPickerModal: PropTypes.func.isRequired,
};
